import React from 'react';

const CONTACT_EMAIL = 'ryanalbanese@advectigroup.com';

const Section = ({ n, title, children }) => (
  <div className="mb-6" data-testid={`app-privacy-section-${n}`}>
    <h2 className="text-xl font-bold text-navy mb-3">§{n}. {title}</h2>
    <div className="text-navy/80 space-y-3 leading-relaxed">{children}</div>
  </div>
);

const AppPrivacy = () => {
  const effectiveDate = 'June 3, 2026';
  const lastUpdated = 'February 15, 2026';
  return (
    <div className="max-w-4xl mx-auto px-4 py-12" data-testid="app-privacy-page">
      <div className="mb-8">
        <p className="text-xs uppercase tracking-widest text-navy/50 mb-1">Advecti Driver — iOS &amp; Android</p>
        <h1 className="text-3xl md:text-4xl font-bold text-navy mb-2">App Privacy Policy</h1>
        <p className="text-sm text-navy/60">Effective date: {effectiveDate} · Last updated: {lastUpdated}</p>
        <p className="text-sm text-navy/60 mt-2">
          This policy applies specifically to the <strong>Advecti Driver</strong> mobile app for iOS and Android.
          For the advertiser web platform and marketing website, see our{' '}
          <a href="/privacy" className="underline text-navy">general privacy policy</a>. The canonical, in-app
          copy of this policy is served at{' '}
          <a href="https://ad-time-admin.emergent.host/api/legal/privacy" className="underline text-navy">
            ad-time-admin.emergent.host/api/legal/privacy
          </a>{' '}
          and mirrored here for App Store review.
        </p>
      </div>

      <Section n={1} title="Who we are">
        <p>
          Advecti (&quot;we,&quot; &quot;our,&quot; or &quot;us&quot;) is a mobile-advertising company headquartered in Columbus, Ohio.
          We operate the <strong>Advecti Driver</strong> mobile application (the &quot;App&quot;), which lets drivers earn
          income by driving their wrapped vehicle within defined geofences. This Privacy Policy explains how we
          collect, use, disclose, and safeguard your information when you use our App.
        </p>
        <p>
          Questions, data-rights requests, or complaints: <a href={`mailto:${CONTACT_EMAIL}`} className="underline">{CONTACT_EMAIL}</a>.
        </p>
      </Section>

      <Section n={2} title="Information we collect">
        <p>We collect the following types of information:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li><strong>Account information:</strong> email address, name, phone number, and authentication credentials when you create an account.</li>
          <li><strong>Vehicle information:</strong> make, model, year, and license plate — used to confirm the wrap is installed on the right vehicle.</li>
          <li><strong>Location data:</strong> real-time GPS coordinates from your device <strong>only while you are in an active driving session</strong>. Essential for calculating earnings based on your position within designated advertising zones.</li>
          <li><strong>BLE beacon data:</strong> Bluetooth Low Energy proximity readings from the beacon paired with your wrap. Used to verify the advertising wrap is physically present on your vehicle during active sessions.</li>
          <li><strong>Driving data:</strong> speed, distance traveled, idle time, and session duration — used for earnings calculation and quality assurance.</li>
          <li><strong>Photo verification data:</strong> photos of your vehicle wrap that you submit during onboarding and periodic verification checks.</li>
          <li><strong>Device information:</strong> device type, operating system, app version, and crash/diagnostic logs — used for troubleshooting and compatibility.</li>
          <li><strong>Push notification tokens:</strong> so we can send tier-upgrade alerts, session-silence reminders, and payout notices.</li>
        </ul>
        <p><strong>What we do NOT collect:</strong> contacts, camera or microphone input outside of the explicit wrap-verification photo flow, HealthKit data, financial account numbers, browsing history on other sites, or advertising identifiers for cross-app tracking.</p>
      </Section>

      <Section n={3} title="How we use your information">
        <ul className="list-disc pl-6 space-y-1">
          <li>Calculate and process your driving and idle earnings based on zone location and time periods.</li>
          <li>Verify BLE beacon connectivity to confirm advertising wrap presence.</li>
          <li>Provide real-time session tracking including speed, zone status, and current earnings.</li>
          <li>Detect peak, off-peak, and no-pay time periods for accurate rate application.</li>
          <li>Determine which earning zone you are in — inner zone, outer zone, idle bubble, or outside zone — and apply the correct pay rate.</li>
          <li>Share anonymized and aggregated campaign-performance data with advertisers (never your name, phone, or exact route).</li>
          <li>Send notifications about session status, earnings updates, and important alerts.</li>
          <li>Improve app performance, fix bugs, and develop new features.</li>
          <li>Comply with legal obligations and resolve disputes.</li>
        </ul>
      </Section>

      <Section n={4} title="Location data — session-only tracking">
        <p>
          Location tracking is <strong>only active during driving sessions that you explicitly start</strong>.
          We do not track your location when you are not in an active session. The App requests
          &quot;When In Use&quot; location permission on iOS; we do <strong>not</strong> collect background location.
        </p>
        <p>Location data is used to:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Determine which earning zone you are in (inner zone, outer zone, idle bubble, or outside zone).</li>
          <li>Calculate distance-based qualifying miles.</li>
          <li>Apply correct pay rates based on your geographic position and the current time period (peak / off-peak / no-pay).</li>
        </ul>
        <p>You can end your session at any time to stop location tracking. You can revoke location permission entirely from your device settings — the App will still open, but you will not be able to log earning sessions.</p>
      </Section>

      <Section n={5} title="Data sharing">
        <p>We may share your information with:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li><strong>Advertising partners:</strong> aggregated session data (driving hours, zones visited, impressions, coarse heatmaps) is shared with campaign advertisers. This data <strong>does not include your name, email, phone number, license plate, or precise route.</strong></li>
          <li><strong>Payment processors:</strong> payment information is shared with <strong>Stripe</strong> for processing withdrawals and identity verification. Stripe&apos;s privacy policy governs their handling of that data.</li>
          <li><strong>Service providers:</strong> we use third-party services for hosting (MongoDB Atlas, Emergent Cloud — both US-region), push-notification delivery (Apple Push Notification Service, Firebase Cloud Messaging), and map tiles (OpenStreetMap). Each processes limited data on our behalf under a data-processing agreement.</li>
          <li><strong>Legal requirements:</strong> we may disclose information when required by law, subpoena, or to protect our rights.</li>
        </ul>
        <p>We do <strong>not</strong> sell your personal data. We do <strong>not</strong> share it with advertisers, ad networks, or data brokers for marketing purposes.</p>
      </Section>

      <Section n={6} title="Third-party SDKs bundled in the App">
        <ul className="list-disc pl-6 space-y-1">
          <li><strong>Expo / React Native runtime</strong> — the framework the App is built on. No data leaves the device via Expo itself.</li>
          <li><strong>expo-location</strong> — accesses GPS while a session is active.</li>
          <li><strong>expo-notifications</strong> — receives push token and displays notifications.</li>
          <li><strong>expo-bluetooth / react-native-ble-plx</strong> — reads BLE beacon proximity from your wrap beacon.</li>
          <li><strong>@react-native-async-storage/async-storage</strong> — stores your session ID and preferences locally.</li>
          <li><strong>Stripe React Native SDK</strong> — securely relays payout details to Stripe.</li>
        </ul>
        <p>We do <strong>not</strong> include ad SDKs, third-party analytics SDKs (no Google Analytics, no Firebase Analytics, no Mixpanel), or cross-app tracking SDKs.</p>
      </Section>

      <Section n={7} title="Data retention">
        <p>
          We retain your session and earnings data for the duration of your campaign participation plus
          <strong> 12 months</strong> for tax, 1099 reporting, and auditing purposes.
        </p>
        <ul className="list-disc pl-6 space-y-1">
          <li><strong>Account information:</strong> until you request deletion, plus a 30-day grace period for reversal.</li>
          <li><strong>Session data (hours, zones, earnings):</strong> 12 months after your last active campaign.</li>
          <li><strong>Precise GPS route points:</strong> 90 days at full precision, then downsampled to coarse (~1 km) grid tiles.</li>
          <li><strong>Photo verification images:</strong> for the duration of your campaign participation, deleted within 30 days after campaign end.</li>
          <li><strong>Crash and diagnostic logs:</strong> 30 days.</li>
        </ul>
        <p>You may request earlier deletion by contacting <a href={`mailto:${CONTACT_EMAIL}`} className="underline">{CONTACT_EMAIL}</a>.</p>
      </Section>

      <Section n={8} title="Data security">
        <p>
          We implement industry-standard security measures including encrypted data transmission (HTTPS / TLS 1.2+),
          encryption at rest (AES-256), secure database storage, HMAC-SHA256-signed webhooks between the App and our backend,
          and access controls (SSO with two-factor authentication for engineers).
        </p>
        <p>
          However, no method of electronic transmission or storage is 100% secure. If we ever detect a breach affecting
          your personal data, we will notify you within 72 hours as required by applicable state law.
        </p>
      </Section>

      <Section n={9} title="Your rights">
        <p>Depending on your jurisdiction, you may have the right to:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Access the personal data we hold about you.</li>
          <li>Request correction of inaccurate data.</li>
          <li>Request deletion of your data.</li>
          <li>Opt out of certain data-processing activities.</li>
          <li>Receive a copy of your data in a portable format (JSON export within 30 days).</li>
          <li>Withdraw location permission at any time via device settings.</li>
          <li>Opt out of push notifications at any time via device settings.</li>
        </ul>
        <p>To exercise any of these rights, email <a href={`mailto:${CONTACT_EMAIL}`} className="underline">{CONTACT_EMAIL}</a> from the address on your account.</p>
      </Section>

      <Section n={10} title="California residents (CCPA/CPRA)">
        <p>
          California residents have specific rights under the California Consumer Privacy Act and California Privacy Rights Act,
          including the right to know, delete, correct, and limit the use of sensitive personal information (which
          includes precise geolocation).
        </p>
        <p>
          To exercise these rights, email <a href={`mailto:${CONTACT_EMAIL}`} className="underline">{CONTACT_EMAIL}</a> from the
          address on your account. We&apos;ll verify your identity and respond within 45 days.
        </p>
        <p>We do <strong>not</strong> sell or share personal information as defined by California law.</p>
      </Section>

      <Section n={11} title="Children's privacy">
        <p>
          The App is not intended for use by individuals under the age of <strong>18</strong>. We do not knowingly
          collect personal information from minors. If we learn we have collected data from anyone under 18, we
          delete it immediately.
        </p>
      </Section>

      <Section n={12} title="US-only service">
        <p>
          The Advecti Driver App is offered <strong>only to residents of the United States</strong>. All data is
          processed and stored on servers located in the United States. We do not intentionally collect, transfer,
          or process data from residents of the European Economic Area, the United Kingdom, or other jurisdictions
          with cross-border data-transfer restrictions.
        </p>
        <p>If you are located outside the United States, please do not use the App. If we learn a non-US resident has created an account, we will delete it and any associated data.</p>
      </Section>

      <Section n={13} title="Changes to this policy">
        <p>
          We may update this Privacy Policy from time to time. We will notify you of material changes (new data
          category, new third party, changed retention period) through an in-app notice at least 14 days before
          the change takes effect, or by email. Your continued use of the App after changes constitutes acceptance.
        </p>
      </Section>

      <Section n={14} title="Governing law">
        <p>
          This policy is governed by the laws of the State of Ohio and applicable US federal law, without regard to
          conflict-of-laws principles. Any disputes will be resolved in the state or federal courts located in
          Franklin County, Ohio.
        </p>
      </Section>

      <Section n={15} title="App Store Nutrition Label (Apple App Privacy Summary)">
        <p>
          This is the data-collection summary Apple displays on the App Store product page. It mirrors what we describe
          in §2–§5 above, formatted as Apple requires. Every category listed here is <strong>linked to your identity</strong> and
          used only for App Functionality — never for tracking across other apps or websites.
        </p>
        <div className="mt-4 space-y-4">
          <div className="border-l-4 border-navy/30 pl-4">
            <p className="font-semibold text-navy">Data Used to Track You</p>
            <p className="text-navy/70">None. We do not use data to track you across apps or websites owned by other companies.</p>
          </div>
          <div className="border-l-4 border-navy/30 pl-4">
            <p className="font-semibold text-navy">Data Linked to You — used only for App Functionality &amp; Analytics</p>
            <ul className="list-disc pl-6 mt-1 space-y-1">
              <li><strong>Contact Info:</strong> Name, Email, Phone Number</li>
              <li><strong>Location:</strong> Precise Location (while session active only)</li>
              <li><strong>Identifiers:</strong> User ID (issued by Advecti)</li>
              <li><strong>Usage Data:</strong> Product Interaction (sessions, hours driven, zones entered)</li>
              <li><strong>User Content:</strong> Vehicle wrap photos (submitted during onboarding and periodic verification)</li>
              <li><strong>Sensitive Info:</strong> Bluetooth (BLE beacon proximity used to verify wrap presence)</li>
              <li><strong>Financial Info:</strong> Payment info handled by Stripe (not stored on our servers)</li>
              <li><strong>Other Data:</strong> Vehicle registration (make, model, year, plate)</li>
            </ul>
          </div>
          <div className="border-l-4 border-navy/30 pl-4">
            <p className="font-semibold text-navy">Data Not Linked to You</p>
            <ul className="list-disc pl-6 mt-1 space-y-1">
              <li><strong>Diagnostics:</strong> Crash Data, Performance Data</li>
            </ul>
          </div>
          <div className="border-l-4 border-navy/30 pl-4">
            <p className="font-semibold text-navy">Purposes</p>
            <p className="text-navy/70">
              App Functionality (delivering the service, calculating pay), Analytics (understanding usage patterns for
              product improvement), and Fraud Prevention. <strong>Not</strong> used for Third-Party Advertising, cross-app
              Advertising or Marketing, or Product Personalization across other companies&apos; apps.
            </p>
          </div>
        </div>
      </Section>

      <Section n={16} title="Contact us">
        <p>
          If you have questions about this Privacy Policy, want to exercise your data rights, or need to report a
          privacy concern, contact us at:
        </p>
        <p>
          <strong>Advecti Group</strong><br />
          Columbus, Ohio, USA<br />
          Email: <a href={`mailto:${CONTACT_EMAIL}`} className="underline">{CONTACT_EMAIL}</a>
        </p>
      </Section>

      <div className="mt-10 pt-6 border-t border-navy/10 text-sm text-navy/50">
        <p>
          Effective {effectiveDate}. This mirrors the canonical in-app copy at{' '}
          <a href="https://ad-time-admin.emergent.host/api/legal/privacy" className="underline">
            ad-time-admin.emergent.host/api/legal/privacy
          </a>. Prior versions available on request.
        </p>
      </div>
    </div>
  );
};

export default AppPrivacy;
